import { useQuery } from '@tanstack/react-query';
import { fetchClientWorkspaceBranding, fetchWorkspaceBranding } from '@/api/branding/brandingApi';
import { WORKSPACE_BRANDING_QUERY_KEY } from '@/constants/queryKeys';
import { WORKSPACE_BRANDING_REFETCH_INTERVAL_MILLISECONDS } from '@/constants/branding';
import { environment } from '@/config/environment';
import { useAuth } from '@/hooks/useAuth';
import { useHostWorkspace } from '@/hooks/useHostWorkspace';

export interface ResolvedBrandingLogos {
  full: string | null;
  tablet: string | null;
  mobile: string | null;
  icon: string | null;
}

export interface ResolvedBranding {
  brandName: string;
  logos: ResolvedBrandingLogos;
  primaryColor: string | null;
}

function resolveNonEmptyStringOrNull(value: string | null): string | null {
  return value !== null && value.trim().length > 0 ? value : null;
}

export function useBranding(): ResolvedBranding {
  const { status, activeScopeCode, activeTenantId } = useAuth();
  const hostWorkspace = useHostWorkspace();
  const isTenantContext = status === 'authenticated' && activeScopeCode === 'tenant';
  const isClientContext = status === 'authenticated' && activeScopeCode === 'client';
  const inWorkspaceContext = isTenantContext || isClientContext;

  const brandingQuery = useQuery({
    queryKey: [...WORKSPACE_BRANDING_QUERY_KEY, 'active', activeScopeCode, activeTenantId],
    queryFn: function loadBranding() {
      return isClientContext ? fetchClientWorkspaceBranding() : fetchWorkspaceBranding();
    },
    enabled: inWorkspaceContext,
    retry: false,
    refetchInterval: WORKSPACE_BRANDING_REFETCH_INTERVAL_MILLISECONDS,
    refetchOnWindowFocus: true,
  });

  const hostBranding = hostWorkspace === null ? undefined : (hostWorkspace.branding ?? undefined);
  const branding = brandingQuery.data ?? hostBranding;
  const resolvedBrandName =
    branding !== undefined && branding.brandName !== null && branding.brandName.trim().length > 0
      ? branding.brandName
      : environment.brandName;

  const workspaceFullLogoUrl =
    branding === undefined ? null : resolveNonEmptyStringOrNull(branding.logos.full);
  const resolvedFullLogoUrl =
    workspaceFullLogoUrl !== null
      ? workspaceFullLogoUrl
      : inWorkspaceContext || hostWorkspace !== null
        ? null
        : resolveNonEmptyStringOrNull(environment.brandLogoUrl);
  const resolvedTabletLogoUrl =
    branding === undefined ? null : resolveNonEmptyStringOrNull(branding.logos.tablet);
  const resolvedMobileLogoUrl =
    branding === undefined ? null : resolveNonEmptyStringOrNull(branding.logos.mobile);
  const resolvedIconLogoUrl =
    branding === undefined ? null : resolveNonEmptyStringOrNull(branding.logos.icon);

  const resolvedPrimaryColor =
    branding !== undefined &&
    branding.primaryColor !== null &&
    branding.primaryColor.trim().length > 0
      ? branding.primaryColor
      : null;

  return {
    brandName: resolvedBrandName,
    logos: {
      full: resolvedFullLogoUrl,
      tablet: resolvedTabletLogoUrl,
      mobile: resolvedMobileLogoUrl,
      icon: resolvedIconLogoUrl,
    },
    primaryColor: resolvedPrimaryColor,
  };
}
